import {
  Component,
  input,
  OnInit,
  OnDestroy,
  AfterContentInit,
  ChangeDetectionStrategy,
  effect,
  untracked,
  InputSignal,
  Signal,
  WritableSignal,
  signal
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { BaseValueAccessor } from '../value-accessor/base-value-accessor';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { BaseSplitButtonComponent } from './base-split-button.component';
import { SplitButtonsRegistration } from './split-buttons-registration.interface';
import type { Subscription } from 'rxjs';

const DEFAULT_COMPARATOR = <T>(obj1: T, obj2: T): boolean => obj1 === obj2;

@Component({
  selector: 'td-wb-split-buttons',
  templateUrl: './split-buttons.component.html',
  styleUrls: ['./split-buttons.component.scss'],
  providers: [
    ValueAccessorProviderFactory(SplitButtonsComponent),
    { provide: SplitButtonsRegistration, useExisting: SplitButtonsComponent }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SplitButtonsComponent<T> extends BaseValueAccessor<T> implements OnDestroy, OnInit, AfterContentInit, SplitButtonsRegistration<T> {
  readonly ariaLabel: InputSignal<string | undefined> = input<string>();
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string>();

  private readonly buttonListSignal: WritableSignal<ReadonlyArray<BaseSplitButtonComponent<T>>> = signal([]);
  readonly buttonList: Signal<ReadonlyArray<BaseSplitButtonComponent<T>>> = this.buttonListSignal.asReadonly();
  readonly comparator: InputSignal<(obj1: T, obj2: T) => boolean> = input<(obj1: T, obj2: T) => boolean>(DEFAULT_COMPARATOR);

  private readonly destroy: Subject<void> = new Subject<void>();
  private subscriptions: Array<Subscription> = [];
  private id: number = 0;

  constructor() {
    super();

    effect((): void => {
      const buttons = this.buttonList();
      if (buttons.length > 0) {
        untracked((): void => {
          this.assignIdToButton();
          this.onButtonSelect();
          Promise.resolve().then((): void => {
            this.setActiveButtonByValue(this.value);
          });
        });
      }
    });
  }

  ngOnInit(): void {
    this.id++;
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
  }

  ngAfterContentInit(): void {
    this.assignIdToButton();
    this.onButtonSelect();
    this.setActiveButtonByValue(this.value);
  }

  registerButton(button: BaseSplitButtonComponent<T>): void {
    this.buttonListSignal.update((current: ReadonlyArray<BaseSplitButtonComponent<T>>): ReadonlyArray<BaseSplitButtonComponent<T>> => [...current, button]);
  }

  unregisterButton(button: BaseSplitButtonComponent<T>): void {
    this.buttonListSignal.update((current: ReadonlyArray<BaseSplitButtonComponent<T>>): ReadonlyArray<BaseSplitButtonComponent<T>> => current.filter((item: BaseSplitButtonComponent<T>): boolean => item !== button));
  }

  getId(): string {
    return 'accessible-split-button-id-' + this.id;
  }

  onButtonSelect(): void {
    this.subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
    this.buttonList().forEach((button: BaseSplitButtonComponent<T>): void => {
      this.subscriptions.push(
        outputToObservable(button.selectChange)
          .pipe(
            takeUntil(this.destroy),
            filter((active: boolean): boolean => active)
          )
          .subscribe((): void => {
            this.setActiveButton(button);
          }));
    });
  }

  override writeValue(value: T): void {
    super.writeValue(value);
    this.setActiveButtonByValue(value);
  }

  private setActiveButtonByValue(value: T): void {
    if (value !== undefined) {
      const button: BaseSplitButtonComponent<T> = this.findButtonByValue(value);
      if (button !== undefined && !button.isActive()) {
        this.setActiveButton(button);
      }
    }
  }

  private setActiveButton(button: BaseSplitButtonComponent<T>): void {
    this.buttonList().forEach((item: BaseSplitButtonComponent<T>): void => {
      item.setActive(item.getButtonId() === button.getButtonId());
    });
    const val = button.value();
    if (val !== undefined) {
      this.value = val;
    }
  }

  private assignIdToButton(): void {
    this.buttonList().forEach((button: BaseSplitButtonComponent<T>, index: number): void => {
      button.setButtonId(index + 1);
      button.setParentId(this.getId());
    });
  }

  private findButtonByValue(value: T): BaseSplitButtonComponent<T> | undefined {
    return this.buttonList()
      .find((button: BaseSplitButtonComponent<T>): boolean => {
        const buttonValue = button.value();
        return buttonValue != null && this.comparator()(buttonValue, value);
      });
  }
}
